import React, { useContext, useMemo, useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import { map, get } from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { Collapse, TextTitle, collapseExpander, useCollapseState, ValidatedPanel } from '@bingads-webui-campaign/wizard-jumbo-card';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup } from '@bingads-webui-react/validation';
import {
  FormErrorGroup, UnmappedFormErrors,
  entityIs, andComp, propertyIs, orComp, anyComp
} from '@bingads-webui-campaign-react/form-error';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { Stack } from '@fluentui/react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { LanguageTargetValidatorFactory, ValidatedLanguageTarget, ValidatedLocationTargetSetting } from '@bingads-webui-campaign-react/step-wizard-controls';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { getPanelErrorProps } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { useFormService } from '@bingads-webui-react/form';
import { campaignLanguageStrings } from '@bingads-webui-campaign-react/language-target';
import { getAdScheduleDetails, getLocationTargetTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidatedLegacyAdSchedule } from '@bingads-webui-campaign/ad-schedule';
import { AudienceInterests } from '@bingads-webui-campaign-react/pmax-targeting';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidatedLegacyDateSetting, getDateSettingDetails } from '@bingads-webui-campaign/date-setting';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';

import { StepHeader } from '../../components/step-header';
import { SectionShimmer } from '../../components/section-shimmer';

import adScheduleIcon from './media/ad-schedule.svg';
import LanguageImg from './media/language.svg';
import LocationImg from './media/location.svg';
import InterestImg from './media/interests.svg';
import StartEndDateImg from './media/start-end-date.svg';

import { getStyle } from './style';
import { WizardScenario } from '../../shared/constants';
import { apiErrorMapper, ERROR_ENTITY } from '../../model/api-errors';
import { useExitWithoutSavingConfirmation } from '../../components/use-exit-without-saving-confirmation';
import { CustomerMatchListExclusion } from '../../components/match-list-exclusion';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const entityIsAssetGroup = entityIs(ERROR_ENTITY.AssetGroup);

const campaignLanguagesProp = andComp(entityIsCampaign, propertyIs('Languages'));
const campaignDayTimeCriterionsProp = entityIs(ERROR_ENTITY.CampaignDayTimeCriterion);
const assetGroupStartEndDateProp = andComp(entityIsAssetGroup, orComp(propertyIs('StartDate'), propertyIs('EndDate')));

const campaignLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);

const ServerValidatedLanguageTarget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLanguageTarget, { filter: campaignLanguagesProp }));
const ServerValidatedLegacyAdSchedule = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyAdSchedule, { filter: campaignDayTimeCriterionsProp }));
const ServerValidatedLegacyDateSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyDateSetting, { filter: assetGroupStartEndDateProp }));
const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLocationTargetSetting, { filter: campaignLocationCriterions }));

const CampaignTargetingStepShellInternal = ({
  data,
  onDataChange,
  error,
  classes,
}) => {
  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);

  const perfMarker = useContext(PerformanceMarkerContext);

  const { scope } = useContext(ScopeContext) || {};

  const { state } = useWizardService();

  const { scenario } = state.context.wizardData;

  const activity = useContext(ActivityContext);

  // const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);

  const currentStates = state.toStrings()[1];

  const { campaign, audiencesSignal, assetGroup } = data || {};

  const { adSchedules } = campaign || {};

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const audienceGroupEnabled = get(serverContext, ['DynamicPermissions', 'WindowsStoreAudienceGroupAssociation']);
  const locationTargetingEnabled = get(serverContext, ['DynamicPermissions', 'WindowsStoreLocationTargeting']);

  const [adScheduleView, setAdScheduleView] = useState(null);
  const setAdScheduleViewCallback = useCallback((view) => {
    setAdScheduleView(view);
  }, []);
  const isCampaignScenario = useMemo(() => scenario === WizardScenario.CampaignCreation || scenario === WizardScenario.CampaignEdit, [scenario]);
  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(i18n);

  const onValidate = (result) => {
    if (data && data.campaign) {
      onDataChange({
        data: {
          ...data,
          campaign: {
            ...data.campaign,
            ...result.data,
          },
          assetGroup: {
            ...data.assetGroup,
            ...result.data,
          },
        },
        isValid: result.isValid,
      });
    }
  };

  const onAudienceSignalChange = useCallback((newAudiences) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        audiencesSignal: {
          ...prevData.audiencesSignal,
          ...newAudiences,
        },
      }),
    });
  }, [onDataChange]);

  const allSections = audienceGroupEnabled ?
    ['locations', 'language-target', 'interests', 'audience-exclusion', 'start-end-dates', 'ad-schedule'] :
    ['locations', 'language-target', 'start-end-dates', 'ad-schedule'];

  const [
    allCampaignTargetingSections,
    campaignTargetingExpandedSections,
    setCampaignTargetingExpandedSections,
  ] = useCollapseState(
    allSections /* all sections */,
    allSections /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignTargetingExpandedSections }),
    [setCampaignTargetingExpandedSections]
  );

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onCampaignStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onCampaignStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  const {
    bundleCountries,
    bundleLocationCriterions,
  } = data || {};

  const locationPanel = useMemo(() => (
    <div className={classNames(classes.locationTargetControl)}>
      <ServerValidatedLocationTargetSetting
        validationProps={{
            data: data && data.campaign && data.campaign.locationTargets,
          }}
        bundleCountries={bundleCountries}
        bundleLocationCriterions={bundleLocationCriterions}
        scope={scope}
        bidStrategyType={campaign && campaign.DisplayBiddingStrategyType}
      />
    </div>
  ), [data, bundleCountries, bundleLocationCriterions, campaign, scope, classes]);

  const fixedLocationPanel = useMemo(() => (
    <div>
      <div className={classNames(classes.locationTargetTitle)}>
        {i18n.getString(_TL_('Where will my ads appear?'))}
      </div>
      <div className={classNames(classes.locationTargetText)}>
        {i18n.getString(_TL_('United States'))}
      </div>
    </div>
  ), [i18n, classes]);

  const onGenericError = useCallback(() => {
    const alertHelper = new AlertHelper({ i18n });
    alertHelper.showError(i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')));
  }, [i18n]);

  const locationsDetails = useMemo(() => getLocationTargetTitleDetails(i18n, campaign && campaign.locationTargets, false, campaign && campaign.BiddingScheme.Type, true), [campaign, i18n]);

  const interestsDetails = useMemo(() => {
    const interestsCount = get(audiencesSignal, ['interests', 'length'], 0);
    if (interestsCount === 0) {
      return i18n.getString(_TL_('None provided'));
    } else if (interestsCount === 1) {
      return i18n.getString(_TL_('{{count}} interest provided'), { count: interestsCount });
    }
    return i18n.getString(_TL_('{{count}} interests provided'), { count: interestsCount });
  }, [audiencesSignal, i18n]);

  const exclusionsDetails = useMemo(() => {
    const exclusionsCount = get(audiencesSignal, ['excludedAudiences', 'length'], 0);
    if (exclusionsCount === 0) {
      return i18n.getString(_TL_('None provided'));
    } else if (exclusionsCount === 1) {
      return i18n.getString(_TL_('{{count}} list provided'), { count: exclusionsCount });
    }
    return i18n.getString(_TL_('{{count}} lists provided'), { count: exclusionsCount });
  }, [audiencesSignal, i18n]);

  return (
    <div className={classNames('campaign-shell', 'targeting-step', classes.targetingPage)}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors, errorMapper: apiErrorMapper }}>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate,
                data: campaign || {},
                i18n: legacyI18n,
            }}
            >
              <div className={classNames(classes.stepContainer, 'campaign-step')}>
                <div>
                  <Stack token={{ childrenGap: 14 }}>
                    <StepHeader
                      title={_TL_('Targets')}
                      description={_TL_('Who do you want seeing your ads? ')}
                    />
                  </Stack>
                </div>
                <div className="other-server-errors">
                  <UnmappedFormErrors />
                </div>
                <Stack horizontal token={{ childrenGap: 14 }}>
                  <Collapse allPanelItems={allCampaignTargetingSections} checkItems skipItemsTypeCheck activeKeys={campaignTargetingExpandedSections} setActiveKeys={setCampaignTargetingExpandedSections}>
                    {
                      isCampaignScenario &&
                      <ValidatedPanel
                        key="locations"
                        title={<TextTitle
                          titleKey={_TL_('Locations')}
                          topicKey="pop_BA_Settings_LocationTargeting"
                          icon={LocationImg}
                          details={locationsDetails}
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        actuationProps={{
                            expand: basicSettingsExpand,
                            expandKey: 'locations',
                          }}
                      >
                        { locationTargetingEnabled ? locationPanel : fixedLocationPanel }
                      </ValidatedPanel>
                    }
                    {
                      isCampaignScenario &&
                      <ValidatedPanel
                        key="language-target"
                        title={<TextTitle
                          titleKey={_TL_('Language')}
                          topicKey="pop_BA_AppAds_Languages"
                          details={campaign && map(campaign.Languages, lang => i18n.getString(campaignLanguageStrings[lang] || lang)).toString()}
                          icon={LanguageImg}
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        actuationProps={{
                            expand: basicSettingsExpand,
                            expandKey: 'language-target',
                          }}
                      >
                        <div className={classNames(classes.languageTargetTitle)}>
                          {i18n.getString(_TL_('Which languages do my customers speak?'))}
                        </div>
                        <ServerValidatedLanguageTarget
                          locale={serverContext.CurrentLocale}
                          permissions={serverContext.Permissions}
                          showLabel={false}
                          validationProps={{
                            field: 'Languages',
                            validator: LanguageTargetValidatorFactory(i18n),
                          }}
                        />
                      </ValidatedPanel>
                    }
                    {scenario !== WizardScenario.CampaignEdit && audienceGroupEnabled &&
                    <ValidatedPanel
                      key="interests"
                      title={
                        <TextTitle
                          titleKey={_TL_('Interests')}
                          icon={InterestImg}
                          details={interestsDetails}
                        />
                          }
                    >
                      <div className={classNames('targets-part', classes.interestsSection)}>
                        <AudienceInterests
                          data={audiencesSignal}
                          onDataChange={onAudienceSignalChange}
                          classes={classes}
                          customHeadline={_TL_('Reach people who are ready to buy:')}
                        />
                      </div>
                    </ValidatedPanel>
                    }
                    {scenario !== WizardScenario.CampaignEdit && audienceGroupEnabled &&
                    <ValidatedPanel
                      key="audience-exclusion"
                      title={
                        <TextTitle
                          titleKey={_TL_('Audience exclusions')}
                          details={exclusionsDetails}
                        />
                          }
                    >
                      <div className={classNames('targets-part', classes.audienceExclusionSection)}>
                        <CustomerMatchListExclusion
                          data={audiencesSignal}
                          onDataChange={onAudienceSignalChange}
                          i18n={i18n}
                          wizardPerfMarker={perfMarker}
                          onGenericError={onGenericError}
                        />
                      </div>
                    </ValidatedPanel>
                    }
                    {
                      scenario !== WizardScenario.CampaignEdit &&
                      <ValidatedPanel
                        key="start-end-dates"
                        title={<TextTitle
                          titleKey={_TL_('Start and end dates')}
                          details={getDateSettingDetails(i18n, assetGroup && assetGroup.StartDate)}
                          icon={StartEndDateImg}
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        {
                          assetGroup ? (
                            <ServerValidatedLegacyDateSetting
                              i18n={i18n}
                              validationProps={{
                                data: {
                                  StartDate: assetGroup ? assetGroup.StartDate : null,
                                  EndDate: assetGroup ? assetGroup.EndDate : null,
                                  TimeZone: campaign && campaign.TimeZone,
                                  isCreate: scenario === WizardScenario.AssetGroupCreation || scenario === WizardScenario.CampaignCreation,
                                },
                              }}
                            />) : (<SectionShimmer />)
                          }
                      </ValidatedPanel>
                    }
                    {
                      isCampaignScenario &&
                      <ValidatedPanel
                        key="ad-schedule"
                        title={<TextTitle
                          titleKey={_TL_('Ad schedule')}
                          key="ad-schedule"
                          panelKey="ad-schedule"
                          details={`${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true)}`}
                          icon={adScheduleIcon}
                        />}
                      >
                        {
                        campaign ? (
                          <ServerValidatedLegacyAdSchedule
                            isCreation
                            entity={{ data: campaign }}
                            validationProps={{
                              data: adSchedules,
                            }}
                            setAdScheduleView={setAdScheduleViewCallback}
                          />
                        ) : (<SectionShimmer />)
                      }
                      </ValidatedPanel>
                    }
                  </Collapse>
                </Stack>
              </div>
            </ValidationGroup>
            {navigationConfirmationPrompt}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

CampaignTargetingStepShellInternal.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      locationTargets: PropTypes.shape({}),
    }).isRequired,
    assetGroup: PropTypes.shape({}).isRequired,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({
    interestsSection: PropTypes.string.isRequired,
    targetingPage: PropTypes.string.isRequired,
    locationTargetTitle: PropTypes.string.isRequired,
    locationTargetText: PropTypes.string.isRequired,
    locationTargetControl: PropTypes.string.isRequired,
    languageTargetTitle: PropTypes.string.isRequired,
    stepContainer: PropTypes.string.isRequired,
    audienceExclusionSection: PropTypes.string.isRequired,
  }).isRequired,
};

export const CampaignTargetingStepShell = withDefaultStyles(CampaignTargetingStepShellInternal, getStyle);
